import React from 'react';
import { CalendarDays, Info, Receipt } from 'lucide-react';
import { formatCents } from '../../../services/freelancerLogic';
import { formatZeitraum } from '../../../services/assignmentLogic';
import {
    CREDIT_NOTE_STATUS_KLASSEN,
    CREDIT_NOTE_STATUS_LABELS,
    FreelancerCreditNote,
    KLEINUNTERNEHMER_HINWEIS,
    faelligAm,
    istKleinunternehmer,
    sortiereCreditNotes,
    summeBruttoCents,
} from '../../../services/freelancerCreditNoteLogic';

// „Meine Gutschriften" im Freelancer-Portal (ERP/CRM-Plan Abschnitt 3.4 —
// „eigene Belege für den Freelancer sichtbar", Phase D5).
//
// Reine Darstellung: die Liste kommt fertig aus dem Container
// (FreelancerPortal), gefiltert wird sie ausschliesslich von der RLS
// (supabase/freelancer_credit_notes.sql, Abschnitt 4b) — sichtbar sind nur die
// EIGENEN, AUSGESTELLTEN Belege. Ein Entwurf ist interner Arbeitsstand und
// erscheint hier nie.
//
// ─── ENTSCHEIDUNG: KEIN PDF-Download ───────────────────────────────────────
// Die Beleg-PDFs liegen im Bucket `invoices` — dem GoBD-Archiv SAEMTLICHER
// Kundenrechnungen. Der Bucket ist admin-only, und das bleibt er: eine
// SELECT-Policy fuer die Rolle `freelancer` waere der erste Lesezugriff einer
// externen Rolle darauf ueberhaupt, und ein Fehler im Pfadausdruck legte den
// gesamten Rechnungsbestand offen. Der Gegenwert waere ein Download-Knopf fuer
// ein Dokument, das der Freelancer ohnehin per E-Mail bekommt.
//
// Statt eines toten Knopfes steht deshalb ein Hinweis in der Liste. Wird das
// spaeter anders entschieden, ist der Ort dafuer ein EIGENER Bucket
// `freelancer-credit-notes` mit Pfadpraefix `<freelancer_id>/` — nicht eine
// Ausnahme im Rechnungsarchiv. Ausfuehrlich: freelancer_credit_notes.sql,
// Abschnitt 7.

interface Props {
    belege: FreelancerCreditNote[];
}

export const FreelancerCreditNotes: React.FC<Props> = ({ belege }) => {
    const sortiert = sortiereCreditNotes(belege);

    if (sortiert.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto">
                    <Receipt className="w-6 h-6 text-slate-300" />
                </div>
                <p className="font-bold text-slate-600 text-sm">Noch keine Gutschrift</p>
                <p className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
                    Abgerechnet wird nach der Prüfung Ihrer Begehungsberichte. Sobald eine
                    Gutschrift ausgestellt ist, erscheint sie hier und Sie erhalten den Beleg
                    per E-Mail.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 px-1">
                <h3 className="text-sm font-bold text-slate-700">Meine Gutschriften</h3>
                {/* Summe ueber ALLE Belege: Storno-Belege tragen negative
                    Betraege und heben ihren Ursprungsbeleg von selbst auf —
                    die Zahl ist damit das, was tatsaechlich geflossen ist bzw.
                    fliessen wird. */}
                <span className="text-[11px] text-slate-500 tabular-nums">
                    Summe {formatCents(summeBruttoCents(sortiert))}
                </span>
            </div>

            {sortiert.map((b) => {
                const faellig = faelligAm(b.ausgestelltAm, b.zahlungszielTage);
                return (
                    <div
                        key={b.id}
                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h4 className="font-bold text-slate-800 text-sm font-mono">
                                    {b.nummer || '—'}
                                </h4>
                                {b.stornoOf && (
                                    <p className="text-[11px] text-rose-600 font-semibold">
                                        Storno – hebt eine frühere Gutschrift auf
                                    </p>
                                )}
                            </div>
                            <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold border shrink-0 ${CREDIT_NOTE_STATUS_KLASSEN[b.status]}`}
                            >
                                {CREDIT_NOTE_STATUS_LABELS[b.status]}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div className="flex items-center gap-2 text-slate-600">
                                <CalendarDays className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                                <span>
                                    {formatZeitraum(b.leistungszeitraumVon, b.leistungszeitraumBis) || '—'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                                <Receipt className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                                <span className="font-semibold tabular-nums">
                                    {formatCents(b.bruttoCents)}
                                </span>
                            </div>
                            <div className="text-slate-600">
                                {b.paidAt
                                    ? `bezahlt am ${formatZeitraum(b.paidAt, b.paidAt)}`
                                    : faellig
                                      ? `Zahlung bis ${formatZeitraum(faellig, faellig)}`
                                      : '—'}
                            </div>
                        </div>

                        {/* Netto/USt nur, wenn es eine Umsatzsteuer gibt. Beim
                            Kleinunternehmer waere eine Zeile „USt 0,00 €"
                            sachlich falsch — es gibt dort keine Steuer, nicht
                            eine in Hoehe von null. */}
                        <div className="text-[11px] text-slate-500 tabular-nums">
                            {istKleinunternehmer(b.steuersatzBp) ? (
                                <span>Netto {formatCents(b.nettoCents)} · {KLEINUNTERNEHMER_HINWEIS}</span>
                            ) : (
                                <span>
                                    Netto {formatCents(b.nettoCents)} · USt {formatCents(b.ustCents)}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}

            <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
                <span>
                    Den Beleg als PDF erhalten Sie per E-Mail. Ein Download ist hier bewusst
                    nicht möglich — die Belegablage enthält auch Unterlagen, die nicht Sie
                    betreffen.
                </span>
            </p>
        </div>
    );
};
